/** @jsxImportSource @emotion/react */
import { useSortable } from "@dnd-kit/sortable";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineMenu } from "react-icons/ai";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { updateSavedSearch } from "../../api/SavedSearches";
import EditTagOutlineIcon from "../../assets/icon/edit-tag-outline";
import PencilOutlineIcon from "../../assets/icon/pencil-outline";
import TrashOutlineIcon from "../../assets/icon/trash-outline";
import { ActiveEmailAtom } from "../../atoms/ActiveEmailAtom";
import { LabelAtom } from "../../atoms/LabelAtom";
import { RecipientAtom } from "../../atoms/RecipientAtom";
import { CountedSavedSearchesAtom } from "../../atoms/SavedSearches";
import { ILabel } from "../../types/LabelTypes";
import { CountedSavedSearches } from "../../types/SavedSearchTypes";
import LabelSelect from "../Label/LabelSelects";

const CardContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 8px 16px;

  border: 1px solid #eceeee;

  :hover .rowActions {
    opacity: 1;
  }

  .rowActions {
    transition: opacity 0.3s ease-in;
    opacity: 0;
  }
`;

const SearchesCard = styled(motion.li)`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: #fff;

  &:first-of-type {
    margin-top: 5px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333333;
`;

const Counter = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: #707070;
`;

const IconAndTitleContainer = styled.div`
  display: flex;
  align-items: flex-start;

  > div:last-of-type {
    top: 7px;
    margin-left: 8px !important;
  }
`;

export const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

interface Props {
  id: string;
  search: CountedSavedSearches;
  editSavedSearch: (search: CountedSavedSearches) => void;
  editTagsSavedSearch: (search: CountedSavedSearches) => void;
  handleSearchDelete: (search: CountedSavedSearches) => void;
}

const initialStyles = {
  x: 0,
  y: 0,
  scale: 1,
};

export default function SavedSearchCard(props: Props): JSX.Element | null {
  const {
    id,
    search,
    editSavedSearch,
    editTagsSavedSearch,
    handleSearchDelete,
  } = props;

  const setActiveEmail = useSetRecoilState(ActiveEmailAtom);
  const labels = useRecoilValue(LabelAtom);
  const countedSavedSearches = useRecoilValue(CountedSavedSearchesAtom);
  const [isLabelSelectOpen, setIsLabelSelectOpen] = useState(false);
  const setCountedSavedSearches = useSetRecoilState(CountedSavedSearchesAtom);
  const selectedRecipient = useRecoilValue(RecipientAtom);

  const { attributes, setNodeRef, listeners, transform, isDragging } =
    useSortable({
      id,
      transition: null,
    });

  /**
   * Draggin with popover usually have issues
   * as the popover appears below the draggable components.
   * For that reason, whenever a popover is spawner, I'll take its parent
   * component and give it a bigger z-index
   *
   * - Palhari
   */
  const draggableContainerRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    setImmediate(() => {
      if (!draggableContainerRef.current) return;

      if (isLabelSelectOpen) {
        draggableContainerRef.current.style.zIndex = "1";
      } else {
        draggableContainerRef.current.style.zIndex = "auto";
      }
    });
  }, [isLabelSelectOpen]);

  async function selectLabelHandler(label: ILabel) {
    try {
      const alreadyInLabel = search.labels.find(
        (searchLabel) => searchLabel === label._id
      );
      let newLabels: string[];

      if (alreadyInLabel) {
        newLabels = search.labels.filter((labelId) => labelId !== label._id);
      } else {
        newLabels = [...search.labels, label._id];
      }

      const isSuccessUpdateLabelSavedSearch = await updateSavedSearch(
        search.id,
        {
          name: search.name,
          publicArchive: search.publicArchive,
          labels: newLabels,
        }
      );

      if (isSuccessUpdateLabelSavedSearch) {
        //If I'm selecting new labels for the currently selected Saved Search,
        //I need to update the activeEmail labels(since they are linked)

        if (selectedRecipient.searchId === search.id) {
          setActiveEmail((prevState) => ({
            ...prevState,
            labels: newLabels,
          }));
        }

        setCountedSavedSearches((s) =>
          s.map((searchFromMap) =>
            searchFromMap.id === search.id
              ? {
                  ...search,
                  labels: newLabels,
                }
              : searchFromMap
          )
        );
      }
    } catch (error) {
      console.log(error);
    }
  }

  const Container = useMemo(() => {
    if (!countedSavedSearches) return null;
    return (
      <CardContainer key={`saved_searches_modal_${search.id}`}>
        <IconAndTitleContainer>
          <div style={{ display: "flex", alignItems: "center" }}>
            <AiOutlineMenu
              {...listeners}
              css={css`
                cursor: grabbing;
                margin-right: 15px;
                color: #bbbbbb;
              `}
            />

            <Column>
              <div style={{ display: "flex" }}>
                <Title>{search.name}</Title>
                <LabelSelect
                  customCss={css`
                    margin-left: 8px;
                    margin-top: 3px;
                  `}
                  activeLabels={
                    labels?.filter((l) => {
                      if (search && search.labels)
                        return search.labels.includes(l._id);
                      else return false;
                    }) || []
                  }
                  selectLabelHandler={selectLabelHandler}
                  onDropViewOpened={(open) => setIsLabelSelectOpen(open)}
                />
              </div>
              <Counter>
                {search.contactsCount}{" "}
                {search.contactsCount > 1 ? "contacts" : "contact"}
              </Counter>
            </Column>
          </div>
        </IconAndTitleContainer>
        <Row
          css={css`
            svg {
              cursor: pointer;
            }

            svg:hover > rect {
              fill: #f9f9f9 !important;
            }
          `}
        >
          <span
            onClick={() => editSavedSearch(search)}
            className="rowActions"
            style={{ display: "flex", alignItems: "center" }}
          >
            <PencilOutlineIcon color={"#979797"} width={32} height={32} />
          </span>

          <span
            onClick={() => editTagsSavedSearch(search)}
            className="rowActions"
            style={{ display: "flex", alignItems: "center", marginLeft: 5 }}
          >
            <EditTagOutlineIcon color={"#FFF"} width={32} height={32} />
          </span>

          <span
            onClick={() => handleSearchDelete(search)}
            className="rowActions"
            style={{ display: "flex", alignItems: "center", marginLeft: 5 }}
          >
            <TrashOutlineIcon color={"#979797"} width={32} height={32} />
          </span>
        </Row>
      </CardContainer>
    );
  }, []);

  return (
    <SearchesCard
      ref={(r) => {
        draggableContainerRef.current = r;
        setNodeRef(r);
      }}
      {...attributes}
      tabIndex={0}
      layoutId={search.id}
      animate={
        transform
          ? {
              x: transform.x,
              y: transform.y,
              scale: isDragging ? 1.05 : 1,
              zIndex: isDragging ? 1 : 0,
              boxShadow: isDragging
                ? "0 0 0 1px rgba(63, 63, 68, 0.05), 0px 15px 15px 0 rgba(34, 33, 81, 0.25)"
                : undefined,
            }
          : initialStyles
      }
      transition={{
        duration: !isDragging ? 0.25 : 0,
        easings: {
          type: "spring",
        },
        scale: {
          duration: 0.25,
        },
        zIndex: {
          delay: isDragging ? 0 : 0.25,
        },
      }}
    >
      {Container}
    </SearchesCard>
  );
}
